<template>
  <div>
    <label class="ts-shadow-rounded ts-uppercase ts-p-2 ts-rounded-md">{{
      $t('quotation.item')
    }}</label>
    <table class="ts-w-full ts-mt-2">
      <thead>
        <tr>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-row-gap-1/12"
          >
            {{ $t('quotation.barcode') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-1/4"
          >
            {{ $t('quotation.productName') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-32"
          >
            {{ $t('quotation.quantity') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-32"
          >
            {{ $t('quotation.uom') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-32"
          >
            {{ $t('quotation.unitPrice') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-32"
          >
            {{ $t('quotation.discountType') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-32"
          >
            {{ $t('quotation.discount') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-32"
          >
            {{ $t('quotation.vatRate') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300 ts-w-64"
          >
            {{ $t('quotation.grandTotal') }}
          </th>
          <th class="ts-uppercase ts-border ts-px-2 ts-py-1 ts-border-gray-300">
            {{ $t('quotation.remark') }}
          </th>
          <th
            class="ts-uppercase ts-border ts-px-2 ts-py-1 ​ts-text-center ts-w-1"
          >
            <a href="#" @click.prevent="() => (show_modal = true)">
              <i class="fa fa-plus"></i>
            </a>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(detail, index) in model.item_list">
          <tr :key="index">
            <td>
              <input
                type="text"
                :value="findProduct(detail.product_id).barcode"
                class="ts-py-1 ts-px-2 ts-w-full ts-outline-none"
                readonly
              />
            </td>
            <td>
              <input
                type="text"
                :value="findProduct(detail.product_id).product_name_en"
                class="ts-py-1 ts-px-2 ts-w-7/12 ts-outline-none"
                readonly
              />
            </td>
            <td>
              <input
                v-model.number="detail.quantity"
                @keypress.enter="onQtyEnter(index)"
                type="number"
                ref="qty"
                class="form-control"
                @input="calculate"
              />
              <div
                class="validate"
                v-if="errors.has('item_list.' + index + '.quantity')"
              >
                {{ errors.first('item_list.' + index + '.quantity') }}
              </div>
            </td>
            <td>
              <a-select
                @keydown.native.enter="onUomEnter(index)"
                v-model="detail.uom_id"
                class="ts-w-full"
                ref="uom"
                :class="{
                  'is-invalid': errors.has('item_list.' + index + '.uom_id')
                }"
                @change="onUomChange(detail)"
              >
                <a-select-option
                  v-for="(u, index) in getUom(detail.product_id)"
                  :key="index"
                  :value="u.uom_id"
                  >{{ u.uom_name_en }}
                </a-select-option>
              </a-select>
            </td>
            <td>
              <input
                v-model.number="detail.unit_price"
                type="text"
                class="form-control"
                readonly
              />
            </td>
            <td>
              <a-select
                v-model="detail.discount_type"
                style="width: 100%"
                :options="[
                  {
                    value: default_currency_sign,
                    label: default_currency_sign
                  },
                  {
                    value: '%',
                    label: '%'
                  }
                ]"
                @change="calculate"
              />
              <div
                class="validate"
                v-if="errors.has('item_list.' + index + '.discount_type')"
              >
                {{ errors.first('item_list.' + index + '.discount_type') }}
              </div>
            </td>
            <td>
              <InputNumber
                v-model.number="detail.discount_rate"
                type="number"
                style="width: 100%;"
                :max="getItemDiscountMax(detail)"
                :min="0"
                @input="calculate"
              />
            </td>
            <td>
              <input
                v-model.number="detail.vat_rate"
                type="text"
                class="form-control"
                @input="calculate()"
              />
            </td>
            <td>
              <input
                v-model="detail.amount"
                type="text"
                readonly
                class="form-control"
              />
            </td>
            <td>
              <input
                v-model.trim="detail.remarks"
                type="text"
                class="form-control"
                @keypress.enter="onRemarkEnter(index)"
              />
              <div
                class="validate"
                v-if="errors.has('item_list.' + index + '.remarks')"
              >
                {{ errors.first('item_list.' + index + '.remarks') }}
              </div>
            </td>
            <td class="ts-text-center" valign="middle">
              <a
                href="#"
                class="ts-text-red-600 ts-my-auto"
                @click.prevent="removeRow(detail)"
              >
                <i class="fa fa-trash"></i>
              </a>
            </td>
          </tr>
          <tr>
            <td
              class="ts-pl-12"
              colspan="9"
              v-html="
                getSpecification({
                  product_id: detail.product_id,
                  uom_id: detail.uom_id
                })
              "
            ></td>
          </tr>
        </template>
        <template v-if="model.item_list.length">
          <tr>
            <td colspan="8" class="text-right">
              <div
                class="ts-border ts-w-32 ts-py-1 ts-px-2 ts-float-right ts-rounded"
              >
                <b>{{ $t('quotation.subTotal') }}</b>
              </div>
            </td>
            <td class="ts-p-2">
              <div
                class="ts-border ts-w-full ts-py-1 ts-px-2 ts-float-right ts-rounded"
              >
                <b>{{ model.sub_total }}</b>
              </div>
            </td>
          </tr>
          <tr>
            <td colspan="8" class="text-right">
              <div
                class="ts-border ts-w-32 ts-py-1 ts-px-2 ts-float-right ts-rounded"
              >
                <b>{{ $t('quotation.discountType') }}</b>
              </div>
            </td>
            <td class="ts-p-2">
              <a-select
                v-model="model.discount_type"
                style="width: 100%"
                :options="[
                  {
                    value: default_currency_sign,
                    label: default_currency_sign
                  },
                  {
                    value: '%',
                    label: '%'
                  }
                ]"
                @change="calculate"
              />
            </td>
          </tr>
          <tr>
            <td colspan="8" class="text-right">
              <div
                class="ts-border ts-w-32 ts-py-1 ts-px-2 ts-float-right ts-rounded"
              >
                <b>{{ $t('quotation.discount') }}</b>
              </div>
            </td>
            <td class="ts-p-2">
              <InputNumber
                v-model.number="model.discount_rate"
                type="number"
                style="width: 100%;"
                :max="getMainDiscountMax(model)"
                :min="0"
                @input="calculate"
              />
            </td>
          </tr>
          <tr>
            <td colspan="8" class="text-right">
              <div
                class="ts-border ts-w-32 ts-py-1 ts-px-2 ts-float-right ts-rounded"
              >
                <b>{{ $t('quotation.grandTotal') }}</b>
              </div>
            </td>
            <td class="ts-p-2">
              <div
                class="ts-border ts-w-full ts-py-1 ts-px-2 ts-float-right ts-rounded"
              >
                <b>{{ model.grand_total }}</b>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <a-modal
      v-if="show_modal"
      v-model="show_modal"
      :title="$t('quotation.productList')"
      :footer="null"
      :maskClosable="false"
      :centered="true"
    >
      <ProductList v-model="model" @calculate="calculate" />
    </a-modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import ProductList from './product-list.vue'

export default {
  name: 'ItemDetails',
  props: ['value', 'validate'],
  components: {
    ProductList
  },
  data() {
    return {
      errors: new Errors(),
      show_modal: false,
      model: {},
      newRow: {
        product_id: '',
        uom_id: '',
        quantity: 1,
        unit_price: '',
        remarks: '',
        amount: 0,
        discount_rate: 0,
        discount_type: '%',
        discount_amount: 0,
        vat_rate: 0,
        vat_amount: 0,
        total_after_discount: 0
      }
    }
  },
  computed: {
    ...mapState('wholesale/quotation', ['customers', 'edit_data']),
    ...mapGetters('wholesale/quotation', [
      'getProduct',
      'getUom',
      'isDefaultUom',
      'getSalePrice',
      'getSpecification',
      'findProduct'
    ]),
    isNotEdit() {
      return this.edit_data.length > 0 || _.isEmpty(this.edit_data)
    },
    default_currency_sign() {
      return this.$store.state.authUser.sys_setting.default_currency_sign
    }
  },
  created() {
    this.model = this.value
  },
  methods: {
    addNewRow() {
      this.model.item_list.push(_.clone(this.newRow))
    },
    removeRow(row) {
      this.model.item_list.splice(this.model.item_list.indexOf(row), 1)
      this.calculate()
    },
    onProductChange(row) {
      row.uom_id = this.isDefaultUom(row.product_id)
      row.unit_price = this.getSalePrice({
        product_id: row.product_id,
        uom_id: row.uom_id
      })
      this.calculate()
    },
    onUomChange(row) {
      row.unit_price = this.getSalePrice({
        product_id: row.product_id,
        uom_id: row.uom_id
      })
      this.calculate()
    },
    calculate() {
      if (this.model.item_list.length > 0) {
        this.model.item_list.forEach(el => {
          const quantity = parseFloat(el.quantity) || 0
          const unit_price = parseFloat(el.unit_price) || 0
          const discount_rate = parseFloat(el.discount_rate) || 0
          const vat_rate = parseFloat(el.vat_rate) || 0

          const total = quantity * unit_price

          // Item Discount
          if (el.discount_type === '%') {
            el.discount_amount = discount_rate
              ? total * _.round(discount_rate / 100, 4)
              : 0
          } else if (el.discount_type === this.default_currency_sign) {
            el.discount_amount = discount_rate
          } else {
            el.discount_amount = 0
          }

          const total_after_discount = total - (el.discount_amount || 0)
          el.total_after_discount = _.round(total_after_discount, 2)

          // VAT
          el.vat_amount = _.round(total_after_discount * (vat_rate / 100), 2)

          // Final Amount
          el.amount = _.round(total_after_discount + el.vat_amount, 2)
        })

        // Sum of all item get subtotal
        const sum = _.sumBy(this.model.item_list, o => o.amount)
        this.model.sub_total = _.round(sum, 2)

        //Apply main discount
        const discountType = this.model.discount_type
        const discountRate = parseFloat(this.model.discount_rate) || 0
        let discountAmount = 0

        if (discountType === '%') {
          discountAmount = _.round(sum * (discountRate / 100), 2)
        } else if (discountType === this.default_currency_sign) {
          discountAmount = discountRate
        }
        // Grand total main after discount
        this.model.grand_total = _.round(sum - discountAmount, 2)
      }
    },

    onUomEnter(index) {
      this.addNewRow()
      this.$nextTick(() => {
        this.$refs.product[index + 1].focus()
      })
    },
    onRemarkEnter(index) {
      this.addNewRow()
      this.$nextTick(() => {
        this.$refs.product[index + 1].focus()
      })
    },
    onQtyEnter(index) {
      this.$nextTick(() => {
        this.$refs.uom[index].focus()
      })
    },
    getItemDiscountMax(detail) {
      if (detail.discount_type === '%') {
        return 100
      } else if (
        detail.discount_type ===
        this.$store.state.authUser.sys_setting.default_currency_sign
      ) {
        const quantity = parseFloat(detail.quantity) || 0
        const unit_price = parseFloat(detail.unit_price) || 0
        return quantity * unit_price
      }
    },
    getMainDiscountMax(model) {
      if (model.discount_type === '%') {
        return 100
      } else if (
        model.discount_type ===
        this.$store.state.authUser.sys_setting.default_currency_sign
      ) {
        return model.grand_total
      }
    }
  },
  watch: {
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>
